import Highcharts from "highcharts";
import { toSafeNumber, toPeriodLabel } from "../data/transformers";
import { LINE_WITH_BAR_CONFIG } from "src/config/charts/lineWithBarChartConfig";

type SeriesDataPoint = {
  x?: string;
  y?: number | string;
  drilldown?: string;
};

type LineWithBarSeriesInput = {
  name?: string;
  type?: "column" | "line";
  yAxis?: number;
  data?: SeriesDataPoint[];
};

type DrilldownPoint = {
  name?: string;
  x?: string;
  y?: number | string;
  drilldown?: string;
};

type DrilldownSeriesInput = {
  id?: string;
  name?: string;
  type?: "column" | "line";
  data?: Array<DrilldownPoint | [string, number]>;
};

type LineWithBarPayload = {
  preserve_category_order?: boolean;
  series?: LineWithBarSeriesInput[];
  drilldown?: { series?: DrilldownSeriesInput[] };
};

const SERIES_COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#14b8a6",
];

export function renderLineWithBar(
  data: unknown,
  spec: unknown,
  title: string,
): Highcharts.Options {
  const payload = !Array.isArray(data) && data && typeof data === "object"
    ? (data as LineWithBarPayload)
    : undefined;
  const seriesData = payload?.series ?? (Array.isArray(data)
    ? (data as LineWithBarSeriesInput[])
    : []);
  const drilldownSeries = payload?.drilldown?.series ?? [];

  const categorySet = new Set<string>();
  seriesData.forEach((s) => {
    (s.data ?? []).forEach((p) => {
      const key = String(p.x ?? "").trim();
      if (key) categorySet.add(key);
    });
  });
  const categories = payload?.preserve_category_order
    ? Array.from(categorySet)
    : Array.from(categorySet).sort();
  const categoryLabels = categories.map(toPeriodLabel);

  const pointCount = categories.length;
  const isLowDensity = pointCount <= 8;
  const isMediumDensity = pointCount > 8 && pointCount <= 16;

  const series: Highcharts.SeriesOptionsType[] = seriesData.map((s, i) => {
    const seriesType = s.type === "line" ? "line" : "column";
    const color = SERIES_COLORS[i % SERIES_COLORS.length];
    const points = categories.map((cat) => {
      const point = (s.data ?? []).find(
        (p) => String(p.x ?? "").trim() === cat,
      );
      return {
        y: toSafeNumber(point?.y),
        ...(point?.drilldown ? { drilldown: point.drilldown } : {}),
      };
    });

    if (seriesType === "line") {
      return {
        type: "line",
        name: String(s.name ?? `Series ${i + 1}`),
        yAxis: s.yAxis ?? 1,
        color,
        data: points as Highcharts.SeriesLineOptions["data"],
        lineWidth: 2,
        zIndex: 2,
        marker: { enabled: true, radius: 3 },
      } as Highcharts.SeriesLineOptions;
    }

    return {
      type: "column",
      name: String(s.name ?? `Series ${i + 1}`),
      yAxis: s.yAxis ?? 0,
      color,
      data: points as Highcharts.SeriesColumnOptions["data"],
    } as Highcharts.SeriesColumnOptions;
  });

  const nativeDrilldownSeries = drilldownSeries.map((child) => ({
    id: String(child.id ?? child.name ?? ""),
    name: String(child.name ?? child.id ?? ""),
    type: child.type === "line" ? "line" : "column",
    data: (child.data ?? []).map((point) => {
      if (Array.isArray(point)) return point;
      return {
        name: String(point.name ?? point.x ?? ""),
        y: toSafeNumber(point.y),
        ...(point.drilldown ? { drilldown: point.drilldown } : {}),
      };
    }),
  }));
  const hasDrilldown = nativeDrilldownSeries.length > 0;
  const categoryStack: string[][] = [];

  const getSeriesCategories = (seriesOptions: any): string[] =>
    (seriesOptions?.data ?? []).map((point: DrilldownPoint | [string, number]) =>
      Array.isArray(point)
        ? String(point[0] ?? "")
        : String(point.name ?? point.x ?? ""),
    );

  return {
    ...LINE_WITH_BAR_CONFIG,
    chart: {
      ...LINE_WITH_BAR_CONFIG.chart,
      ...(pointCount > 16
        ? {
            scrollablePlotArea: {
              minWidth: Math.max(900, pointCount * 56),
              scrollPositionX: 1,
            },
          }
        : {}),
    },
    // title: { text: title, align: "left" },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    xAxis: {
      type: "category",
      categories: categoryLabels,
      labels: {
        rotation: pointCount > 12 ? -35 : 0,
        style: { fontSize: "11px" },
      },
    },
    yAxis: [
      {
        min: 0,
        title: { text: "Count" },
      },
      {
        min: 0,
        max: 100,
        title: { text: "Rate (%)" },
        opposite: true,
        labels: { format: "{value}%" },
      },
    ],
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        pointPadding: isLowDensity ? 0.01 : isMediumDensity ? 0.02 : 0.03,
        groupPadding: isLowDensity ? 0.015 : isMediumDensity ? 0.025 : 0.04,
        maxPointWidth: isLowDensity ? 56 : isMediumDensity ? 34 : 24,
        minPointLength: 2,
        cursor: hasDrilldown ? "pointer" : undefined,
        dataLabels: {
          enabled: hasDrilldown && pointCount <= 8,
          formatter: function formatter(this: any) {
            return toSafeNumber(this.y).toLocaleString();
          },
        },
      },
      series: {
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter: function (this: any) {
        const hoveredLabel = String(
          this.points?.[0]?.point?.category ?? this.x ?? "",
        );
        const rows = (this.points ?? [])
          .map((point: any) => {
            const val = toSafeNumber(point.y);
            const isLine = point.series.type === "line";
            const formatted = isLine ? `${val}%` : val.toLocaleString();
            return `<div style="display:flex;justify-content:space-between;gap:12px;font-size:12px;margin-top:2px;"><span><span style="color:${point.color}">●</span> ${point.series.name}</span><b>${formatted}</b></div>`;
          })
          .join("");
        const drillHint = (this.points ?? []).some(
          (point: any) => point.point?.options?.drilldown,
        )
          ? `<div style="margin-top:6px;font-size:11px;color:#6366f1;">Click to drill down</div>`
          : "";
        return `<div style="padding:8px 10px;min-width:200px;"><div style="font-size:12px;font-weight:700;margin-bottom:4px;">${hoveredLabel}</div>${rows}${drillHint}</div>`;
      },
    },
    ...(hasDrilldown
      ? {
          drilldown: {
            series: nativeDrilldownSeries as Highcharts.DrilldownSeriesOptions[],
            events: {
              drilldown: function drilldown(event: any) {
                const chart = this as Highcharts.Chart;
                const axis = chart.xAxis[0];
                categoryStack.push(
                  (axis.categories ?? categoryLabels).map(String),
                );
                axis.setCategories(
                  getSeriesCategories(event.seriesOptions),
                  false,
                );
                chart.redraw();
              },
              drillup: function drillup() {
                const chart = this as Highcharts.Chart;
                const previousCategories = categoryStack.pop();
                if (previousCategories) {
                  chart.xAxis[0].setCategories(previousCategories, false);
                  chart.redraw();
                }
              },
            },
          },
        }
      : {}),
    series,
  };
}
